import React, { useState, useRef, useEffect } from 'react';
import { trackTLDSelection } from '../utils/analytics';

interface SearchBarProps {
  onSearch: (domain: string) => void;
  isLoading?: boolean;
}

const TLD_OPTIONS = ['.lt', '.com', '.ai', '.org', '.io', '.pro'];

const SearchBar: React.FC<SearchBarProps> = ({ onSearch, isLoading = false }) => {
  const [domainName, setDomainName] = useState('');
  const [selectedTLD, setSelectedTLD] = useState('.lt');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedDomain = domainName.trim();
    if (trimmedDomain) {
      // Check if the input already contains a dot (indicating a full domain)
      if (trimmedDomain.includes('.')) {
        onSearch(trimmedDomain);
      } else {
        onSearch(trimmedDomain + selectedTLD);
      }
    }
  };

  const handleTLDSelection = (tld: string) => {
    setSelectedTLD(tld);
    // Track TLD selection
    trackTLDSelection(tld);
  };

  return (
    <div className="w-full max-w-4xl mx-auto sm:px-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 px-4 sm:px-0">
          <input
            ref={inputRef}
            type="text"
            value={domainName}
            onChange={e => setDomainName(e.target.value)}
            placeholder="Enter a domain (e.g., example or example.com)"
            className="flex-grow p-4 text-lg border border-gray-300 rounded-2xl shadow focus:shadow-lg focus:ring-4 focus:ring-blue-300 focus:border-blue-600 transition-all duration-200 bg-white outline-none"
            disabled={isLoading}
          />
          <button
            type="submit"
            className="px-7 py-3 text-lg bg-blue-600 text-white rounded-2xl shadow-md hover:bg-blue-700 active:scale-95 disabled:bg-gray-400 transition-all duration-150 focus:outline-none focus:ring-4 focus:ring-blue-300"
            disabled={isLoading || !domainName.trim()}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                Searching...
              </span>
            ) : (
              'Check'
            )}
          </button>
        </div>

        {!domainName.includes('.') && (
          <div className="flex flex-wrap gap-2 justify-center">
            {TLD_OPTIONS.map(tld => (
              <button
                key={tld}
                type="button"
                onClick={() => handleTLDSelection(tld)}
                className={`px-4 py-2 rounded-full border-2 transition-colors ${
                  selectedTLD === tld
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                {tld}
              </button>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
